import { CombinedGraphQLErrors, CombinedProtocolErrors, ServerError } from "@apollo/client/errors";
import { ErrorLink } from "@apollo/client/link/error";
import * as Sentry from "@sentry/nextjs";
import { isExpectedTinglysningGraphQLError } from "@/lib/apollo/graphql-field-errors";

/**
 * Shared error link used by both the server and browser Apollo clients.
 *
 * Logging strategy: structured console output stays the dev signal. In
 * deployed environments unexpected failures are also forwarded to Sentry —
 * `captureException` is a no-op when the SDK never initialised (DSN unset
 * locally), so this import is safe everywhere.
 *
 * Auth lifecycle errors (401 / `UNAUTHENTICATED`) are deliberately NOT
 * captured — they fire normally during session expiry and would drown the
 * project in noise that doesn't represent a bug.
 *
 * Apollo Client v4 unifies errors under a single `error` union — use the
 * `Combined*Errors.is` / `ServerError.is` guards to discriminate.
 *
 * Auth behaviour: when the server says the caller is unauthenticated we
 * kick the browser back to `/login` so the Auth0 Universal Login flow can
 * mint a fresh session. The server-side client skips the redirect — RSC
 * code paths already go through `requireSession()` and hit this link only
 * when the proxy / router is mid-refresh, so a redirect would be harmful.
 */

const LOGIN_PATH = "/login";

// Concurrent queries that all fail on the same session expiry would each
// trigger a navigation if we weren't careful. Latch once per page and let
// `window.location.assign` tear down everything in flight.
let redirectInFlight = false;

function redirectToLogin() {
    if (typeof window === "undefined") return;
    if (redirectInFlight) return;

    const { pathname, search, hash } = window.location;
    // Already on (or in the middle of leaving to) the login page — no-op so
    // we don't thrash history or create a `/login?returnTo=/login` loop.
    if (pathname.startsWith(LOGIN_PATH)) return;

    redirectInFlight = true;
    const returnTo = `${pathname}${search}${hash}` || "/";
    window.location.assign(`${LOGIN_PATH}?returnTo=${encodeURIComponent(returnTo)}`);
}

function isUnauthenticatedGraphQLError(error: unknown): boolean {
    if (!CombinedGraphQLErrors.is(error)) return false;
    return error.errors.some((err) => err.extensions?.code === "UNAUTHENTICATED");
}

/**
 * Codes a service returns for a request the user can fix (a taken name, the last administrator
 * leaving, a group still in use, invalid input). The UI shows them inline; they are not bugs, so
 * they are logged as warnings and kept out of Sentry.
 */
const EXPECTED_DOMAIN_ERROR_CODES: ReadonlySet<string> = new Set(["GROUP_NAME_TAKEN", "LAST_ADMIN", "GROUP_IN_USE"]);

function isExpectedDomainGraphQLError(error: { extensions?: Record<string, unknown> | null }): boolean {
    const code = error.extensions?.code;
    return typeof code === "string" && EXPECTED_DOMAIN_ERROR_CODES.has(code);
}

export const errorLink = new ErrorLink(({ error, operation }) => {
    const op = operation.operationName ?? "anonymous";

    // Replacing a search deliberately aborts its fetch. Preserve the error for
    // its subscriber, but do not report expected cancellation as an outage.
    if (error.name === "AbortError" && operation.getContext().fetchOptions?.signal?.aborted === true) return;

    // 1) Cosmo (or our proxy) returned a GraphQL-shape response whose
    //    extensions flag the session as dead. Ship the user to /login.
    if (isUnauthenticatedGraphQLError(error)) {
        console.warn(`[Auth] ${op}: session expired, redirecting to login`);
        redirectToLogin();
        return;
    }

    if (CombinedGraphQLErrors.is(error)) {
        for (const err of error.errors) {
            if (isExpectedTinglysningGraphQLError(err)) {
                console.warn(`[GraphQL] ${op}: ${err.message} (Tinglysning unavailable)`, {
                    path: err.path,
                    code: err.extensions?.code,
                });
                continue;
            }

            if (isExpectedDomainGraphQLError(err)) {
                console.warn(`[GraphQL] ${op}: ${err.message}`, { path: err.path, code: err.extensions?.code });
                continue;
            }

            console.error(`[GraphQL] ${op}: ${err.message}`, {
                path: err.path,
                code: err.extensions?.code,
            });
            Sentry.captureException(err, {
                tags: {
                    source: "apollo.graphql",
                    operation: op,
                    code: typeof err.extensions?.code === "string" ? err.extensions.code : "unknown",
                },
                extra: { path: err.path },
            });
        }
        return;
    }

    if (CombinedProtocolErrors.is(error)) {
        for (const err of error.errors) {
            console.error(`[Protocol] ${op}: ${err.message}`, err.extensions);
            Sentry.captureException(err, {
                tags: { source: "apollo.protocol", operation: op },
                extra: { extensions: err.extensions },
            });
        }
        return;
    }

    // 2) Non-2xx HTTP response that Apollo couldn't parse as GraphQL. Our
    //    `/api/graphql` proxy sends 401 with plain `application/json` when
    //    the session cookie is missing — that lands here.
    if (ServerError.is(error)) {
        if (error.statusCode === 401) {
            console.warn(`[Auth] ${op}: 401 from GraphQL proxy, redirecting to login`);
            redirectToLogin();
            return;
        }
        console.error(`[Server ${error.statusCode}] ${op}: ${error.message}`, { body: error.bodyText });
        Sentry.captureException(error, {
            tags: {
                source: "apollo.server",
                operation: op,
                status: String(error.statusCode),
            },
            extra: { bodyText: error.bodyText },
        });
        return;
    }

    console.error(`[Network] ${op}:`, error);
    Sentry.captureException(error, {
        tags: { source: "apollo.network", operation: op },
    });
});

// Exported for tests — resetting this between assertions keeps the latch
// from leaking state across specs. Not part of the runtime API.
export const __resetAuthRedirectLatch = () => {
    redirectInFlight = false;
};
